import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, FileText, Trash2, Pencil, Download } from 'lucide-react';
import { SortableList } from './SortableList';
import { Dropzone } from './Dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolve = (src) => (!src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`);

const AnnualReportsManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAll = async () => {
    const res = await adminApi.get('/annual-reports');
    setItems(res.data || []);
  };
  useEffect(() => { fetchAll(); }, []);

  const blank = () => ({ title: '', year: String(new Date().getFullYear()), description: '', file_url: '', cover_image: '', order: items.length, published: true });

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title?.trim()) return toast({ title: 'Title required', variant: 'destructive' });
    if (!editing.file_url) return toast({ title: 'Please upload the report PDF', variant: 'destructive' });
    setLoading(true);
    try {
      if (editing.id && items.find(x => x.id === editing.id)) {
        await adminApi.put(`/annual-reports/${editing.id}`, editing);
      } else {
        await adminApi.post('/annual-reports', { ...editing, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchAll();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    const it = items.find(x => x.id === id);
    if (!confirm(`Delete "${it?.title || 'this report'}"? This cannot be undone.`)) return;
    await adminApi.delete(`/annual-reports/${id}`);
    toast({ title: 'Deleted' });
    fetchAll();
  };

  const reorder = async (list) => {
    setItems(list);
    await adminApi.put('/annual-reports/reorder', { ids: list.map(r => r.id) });
  };

  return (
    <div className="space-y-6" data-testid="annual-reports-manager">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#1565C0]" />
          <div>
            <h2 className="text-2xl font-bold">Annual Reports</h2>
            <p className="text-xs text-gray-500">Upload and manage annual reports, financials and impact reports (PDF).</p>
          </div>
        </div>
        <Button onClick={() => setEditing(blank())} className="bg-[#1565C0]" data-testid="add-annual-report-btn">
          <Plus className="w-4 h-4 mr-2" /> Upload Annual Report
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle>{editing.id ? 'Edit' : 'Upload'} Annual Report</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={editing.title} onChange={(e)=>setEditing({...editing, title:e.target.value})} required data-testid="ar-title" placeholder="EWRF Annual Report 2025" /></div>
                <div><Label>Year</Label><Input value={editing.year} onChange={(e)=>setEditing({...editing, year:e.target.value})} data-testid="ar-year" placeholder="2025" /></div>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea value={editing.description} onChange={(e)=>setEditing({...editing, description:e.target.value})} rows={3} placeholder="Highlights of the year's programmes and financials..." />
              </div>
              <div>
                <Label>Report File (PDF)</Label>
                {editing.file_url && (
                  <div className="mb-2 flex items-center gap-2">
                    <a href={resolve(editing.file_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1565C0] hover:underline flex items-center gap-1">
                      <Download className="w-3 h-3" /> {editing.file_url}
                    </a>
                    <Button type="button" size="sm" variant="outline" onClick={()=>setEditing({...editing, file_url:''})}>Remove</Button>
                  </div>
                )}
                <Dropzone compact accept="application/pdf,.pdf,.doc,.docx" onUpload={(url)=>setEditing({...editing, file_url:url})} label="Drop annual report PDF here" />
              </div>
              <div>
                <Label>Cover Image (optional)</Label>
                {editing.cover_image && (
                  <div className="mb-2 flex items-center gap-2">
                    <img src={resolve(editing.cover_image)} alt="cover" className="w-24 h-32 object-cover rounded border" />
                    <Button type="button" size="sm" variant="outline" onClick={()=>setEditing({...editing, cover_image:''})}>Remove</Button>
                  </div>
                )}
                <Dropzone compact onUpload={(url)=>setEditing({...editing, cover_image:url})} label="Optional cover image (JPG/PNG)" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="ar-pub" checked={editing.published !== false} onCheckedChange={(v)=>setEditing({...editing, published:v})} />
                <Label htmlFor="ar-pub">Published (visible on public page)</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-annual-report-btn">{loading ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" onClick={()=>setEditing(null)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList items={items} onReorder={reorder} renderItem={(r) => (
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
            <div className="w-16 h-20 bg-gray-100 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
              {r.cover_image ? <img src={resolve(r.cover_image)} className="w-full h-full object-cover" alt="" /> : <FileText className="w-6 h-6 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{r.title}</h3>
              <p className="text-xs text-gray-500">{r.year}</p>
              {r.file_url && <a href={resolve(r.file_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1565C0] hover:underline">Preview PDF</a>}
              {!r.published && <span className="ml-2 text-[10px] uppercase text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Hidden</span>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={()=>setEditing(r)}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={()=>del(r.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )} />
      {items.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">No reports yet. Click "Upload Annual Report" to add one.</p>
      )}
    </div>
  );
};

export default AnnualReportsManager;
